import React, { useMemo } from 'react';
import { Segmented } from 'antd';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useTranslation } from 'react-i18next';

dayjs.extend(isoWeek);

/**
 * Routes-page quick date filter — one-click presets for the date axis
 * dispatchers filter by most. Sits on the toolbar next to Search; the
 * Filter drawer keeps the precise custom range + driver/status.
 *
 * Each preset resolves to a { dateFrom, dateTo } YYYY-MM-DD window that the
 * backend's GET /routes `dateFrom`/`dateTo` params already understand. "All"
 * clears the date filter. Selecting a custom range in the Filter drawer
 * deselects every chip (no preset matches), so the two stay in sync.
 *
 * Dates are computed in the browser's local time — for OVO's Swiss operators
 * that's Europe/Zurich, matching what they see elsewhere.
 */

const FMT = 'YYYY-MM-DD';

export function resolveRoutePreset(key) {
  const today = dayjs();
  switch (key) {
    case 'tomorrow': {
      const d = today.add(1, 'day').format(FMT);
      return { dateFrom: d, dateTo: d };
    }
    case 'today': {
      const d = today.format(FMT);
      return { dateFrom: d, dateTo: d };
    }
    case 'yesterday': {
      const d = today.subtract(1, 'day').format(FMT);
      return { dateFrom: d, dateTo: d };
    }
    case 'this_week':
      return {
        dateFrom: today.startOf('isoWeek').format(FMT),
        dateTo: today.endOf('isoWeek').format(FMT),
      };
    // #134 — the monthly HR export window (previous calendar month).
    case 'last_month': {
      const m = today.subtract(1, 'month');
      return { dateFrom: m.startOf('month').format(FMT), dateTo: m.endOf('month').format(FMT) };
    }
    case 'all':
    default:
      return { dateFrom: undefined, dateTo: undefined };
  }
}

// Which preset (if any) the current dateFrom/dateTo correspond to. Returns
// 'all' when there's no date filter, a preset key on an exact match, or null
// for a custom range set via the drawer (→ no chip highlighted).
function matchPreset(dateFrom, dateTo) {
  if (!dateFrom && !dateTo) return 'all';
  for (const key of ['tomorrow', 'today', 'yesterday', 'this_week', 'last_month']) {
    const r = resolveRoutePreset(key);
    if (r.dateFrom === dateFrom && r.dateTo === dateTo) return key;
  }
  return null;
}

export default function RouteDateQuickFilter({ dateFrom, dateTo, onChange }) {
  const { t } = useTranslation('routes');
  const active = useMemo(() => matchPreset(dateFrom, dateTo), [dateFrom, dateTo]);

  const options = [
    { value: 'tomorrow', label: t('quickTomorrow') },
    { value: 'today', label: t('quickToday') },
    { value: 'yesterday', label: t('quickYesterday') },
    { value: 'this_week', label: t('quickThisWeek') },
    { value: 'last_month', label: t('quickLastMonth') },
    { value: 'all', label: t('quickAll') },
  ];

  return (
    <Segmented
      size="small"
      options={options}
      value={active || undefined}
      onChange={(key) => onChange(resolveRoutePreset(key))}
    />
  );
}
